import { marked } from 'marked'
import { Reference } from '../../types/chatTypes'

export const convertYoutubeUrlToEmbed = (url: string): string => {
  // Check if it's already an embed URL
  if (url.includes('youtube.com/embed/')) {
    return `<iframe width="300" height="169" src="${url}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`
  }

  // Convert standard YouTube URLs to embed format
  const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/
  const match = url.match(youtubeRegex)

  if (match && match[1]) {
    const embedUrl = `https://www.youtube.com/embed/${match[1]}`
    return `<iframe width="300" height="169" src="${embedUrl}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`
  }

  return url
}

export const isYoutubeLink = (url: string): boolean => {
  return url.includes('youtube.com/embed/') || url.includes('youtube.com/watch') || url.includes('youtu.be/')
}

export const isSharePointLink = (url: string): boolean => {
  return url.includes('sharepoint.com') || url.includes('.sharepoint.') || url.includes('office.com')
}

export const parseMarkdownToHTML = (markdownText: string): string => {
  const parsedMarkdownToHTML = marked.parse(markdownText, {}) as string

  const fragments = parsedMarkdownToHTML.split(/\[([^\]]+)\]/g)
  let processedHtml = fragments.join('')

  // Process any YouTube links in the HTML
  processedHtml = processedHtml.replace(/<a href="([^"]+)"[^>]*>([^<]+)<\/a>/g, (match, url, text) => {
    if (isYoutubeLink(url)) {
      return convertYoutubeUrlToEmbed(url)
    }
    return match
  })

  return processedHtml
}

export const parseCustomMarkdown = (markdownText: string): string => {
  let parsedMarkdown = marked.parseInline(markdownText, {}) as string
  parsedMarkdown = parsedMarkdown.replace('-', '&#9;-')

  return parsedMarkdown
}

export const parseUrlToHtml = (url: string, text: string) => {
  return `<a href="${url}" style="color: blue; text-decoration: underline;" target="_blank">${text}</a>`
}

const extractTitleFromUrl = (url: string): string => {
  try {
    const decodedUrl = decodeURIComponent(url)

    const lastPart = decodedUrl.split('/').pop() || ''

    const pathWithoutQuery = lastPart.split('?')[0]

    const titleWithoutExtension = pathWithoutQuery.split('.').slice(0, -1).join('.')

    return titleWithoutExtension || pathWithoutQuery
  } catch (error) {
    console.error('Error extracting title from URL:', error)
    return 'Untitled'
  }
}

export const formatReferencesMarkdown = (references: Reference[]): string => {
  if (!references.length) return ''

  const sourcesSection = references
    .map(ref => {
      const displayTitle = ref.title || extractTitleFromUrl(ref.url)
      return `**Bron:** [${displayTitle}](${ref.url})`
    })
    .join('\n\n')

  const videosWithUrls = references.filter(ref => ref.video_url && ref.video_url.trim() !== '')

  const videosSection =
    videosWithUrls.length > 0
      ? '\n\n' +
        videosWithUrls
          .map(ref => {
            const videoUrl = ref.video_url || ''
            const embedUrl = videoUrl.replace(
              /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/,
              'https://www.youtube.com/embed/$1',
            )
            return embedUrl
          })
          .join('\n')
      : ''

  return `\n\n${sourcesSection}${videosSection}\n`
}
